/**
 * MfaPanel — user self-serve MFA (TOTP): status + enroll + disable + sign-out-all.
 *
 * Shown in Profile page. Handles the initial-enrolment flow (reveals secret,
 * QR, and one-time recovery codes) and the ongoing management flow.
 */
import React, { useEffect, useState } from 'react';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MfaPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Enrolment state
  const [setup, setSetup] = useState(null);   // {secret, uri, qr_data_url, recovery_codes}
  const [code, setCode] = useState('');
  const [revealRecovery, setRevealRecovery] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null); // {kind:'disable'|'signout', message, onConfirm}

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/api/auth/mfa/status`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus(await r.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const beginSetup = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/api/auth/mfa/setup`, { method: 'POST' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(formatBackendDetail(err.detail) || `HTTP ${r.status}`);
      }
      setSetup(await r.json());
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const verifySetup = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(formatBackendDetail(err.detail) || `HTTP ${r.status}`);
      }
      setSetup(null); setCode(''); setRevealRecovery(false);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const disable = () => setPendingConfirm({
    kind: 'disable',
    message: 'Disable MFA? Platform policy may force you back on at next sign-in.',
    confirmLabel: 'Yes, disable MFA',
    onConfirm: async () => {
      setBusy(true); setError(null);
      try {
        const r = await fetch(`${API_URL}/api/auth/mfa`, { method: 'DELETE' });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(formatBackendDetail(err.detail) || `HTTP ${r.status}`);
        }
        setPendingConfirm(null);
        await load();
      } catch (e) { setError(e.message); setPendingConfirm(null); }
      finally { setBusy(false); }
    },
  });

  const signOutEverywhere = () => setPendingConfirm({
    kind: 'signout',
    message: 'Sign out of all sessions on every device? You will need to sign in again.',
    confirmLabel: 'Yes, sign out everywhere',
    onConfirm: async () => {
      setBusy(true);
      try {
        await fetch(`${API_URL}/api/auth/sessions/revoke-mine`, { method: 'POST' });
        window.location.href = '/login';
      } catch (e) { setError(e.message); setBusy(false); setPendingConfirm(null); }
    },
  });

  return (
    <div className="panel" style={{ marginBottom: 16 }} data-testid="mfa-panel">
      <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Multi-factor authentication</span>
        {status && (
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase',
            letterSpacing: '0.05em',
            background: status.enabled ? '#dcfce7' : '#fef3c7',
            color: status.enabled ? '#166534' : '#92400e',
          }}>{status.enabled ? 'ENABLED' : 'OFF'}</span>
        )}
      </div>
      <div className="panel-body">
        {loading && <div>Loading…</div>}
        {error && <div className="auth-error" data-testid="mfa-error">{error}</div>}

        {!loading && status && !setup && (
          <>
            {!status.enabled && !status.setup_in_progress && (
              <>
                <p style={{ fontSize: 13, marginTop: 0 }}>
                  Protect your account with a second factor. Use Google Authenticator, 1Password,
                  Authy or any TOTP-compatible app.
                </p>
                <button className="btn btn-primary btn-sm" onClick={beginSetup} disabled={busy}
                        data-testid="mfa-begin-btn">
                  {busy ? 'Starting…' : 'Enable MFA'}
                </button>
              </>
            )}
            {status.enabled && (
              <>
                <p style={{ fontSize: 13, marginTop: 0 }}>
                  MFA is active. Recovery codes remaining: <strong>{status.recovery_codes_remaining}</strong>.
                  {status.last_used_at && ` Last used ${new Date(status.last_used_at).toLocaleString()}.`}
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={disable} disabled={busy}
                          data-testid="mfa-disable-btn">Disable</button>
                  <button className="btn btn-outline btn-sm" onClick={signOutEverywhere} disabled={busy}
                          data-testid="mfa-signout-all-btn">Sign out everywhere</button>
                </div>
                {pendingConfirm && (
                  <div className="panel" style={{ marginTop: 12, borderColor: 'var(--danger)' }}
                       data-testid={`mfa-confirm-${pendingConfirm.kind}`}>
                    <div className="panel-body" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13 }}>{pendingConfirm.message}</span>
                      <button className="btn btn-primary btn-sm" style={{ background: 'var(--danger)' }}
                              disabled={busy}
                              onClick={pendingConfirm.onConfirm}
                              data-testid={`mfa-confirm-${pendingConfirm.kind}-btn`}>
                        {busy ? 'Working…' : pendingConfirm.confirmLabel}
                      </button>
                      <button className="btn btn-outline btn-sm"
                              onClick={() => setPendingConfirm(null)}
                              data-testid={`mfa-confirm-${pendingConfirm.kind}-cancel`}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Enrolment dialog */}
        {setup && (
          <div data-testid="mfa-setup-dialog" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                1. Scan with your authenticator app
              </h4>
              <img src={setup.qr_data_url} alt="TOTP QR" style={{ width: 180, height: 180, background: '#fff', padding: 8, border: '1px solid var(--border)', borderRadius: 4 }} />
              <div style={{ fontSize: 11, marginTop: 8, color: 'var(--text-secondary)' }}>
                Manual key:
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'var(--text)', wordBreak: 'break-all' }}
                     data-testid="mfa-setup-secret">
                  {setup.secret}
                </div>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 260 }}>
              <h4 style={{ margin: '0 0 4px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                2. Save your recovery codes
              </h4>
              <div style={{ background: 'var(--bg)', padding: 8, borderRadius: 4, fontSize: 11, marginBottom: 8 }}>
                Keep these somewhere safe — each can be used once if you lose your device.
                They will NOT be shown again.
              </div>
              {!revealRecovery ? (
                <button className="btn btn-outline btn-sm" onClick={() => setRevealRecovery(true)}
                        data-testid="mfa-reveal-recovery-btn">
                  Reveal recovery codes
                </button>
              ) : (
                <pre data-testid="mfa-recovery-codes" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, background: 'var(--bg)', padding: 8, borderRadius: 4, margin: 0, whiteSpace: 'pre-wrap' }}>
                  {setup.recovery_codes.join('\n')}
                </pre>
              )}

              <h4 style={{ margin: '16px 0 4px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                3. Enter the 6-digit code
              </h4>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)}
                     placeholder="123456" maxLength={6}
                     data-testid="mfa-setup-code-input"
                     style={{ width: 120, fontFamily: "'IBM Plex Mono', monospace", fontSize: 16, letterSpacing: 4, padding: 8, border: '1px solid var(--border)', borderRadius: 4 }} />
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={verifySetup} disabled={busy || code.length < 6}
                        data-testid="mfa-setup-submit-btn">
                  {busy ? 'Verifying…' : 'Activate MFA'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => { setSetup(null); setCode(''); }}
                        data-testid="mfa-setup-cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
